import BasketSummary from '@components/Reservation/BasketSummary';
import Button from '@components/_Layout/Controls/Button';
import React, { Fragment, useState } from 'react';
import toast from 'react-hot-toast';
import { Dialog, Transition } from '@headlessui/react';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getTotalReservationsPrice } from '@utils/prices';
import { Link } from 'react-router-dom';
import { routes } from '@routes';
import { useCart, useLoadingScreen, useSession } from '@hooks';


type CartOverlayProps = {
  btnElement: (onClick: () => void) => JSX.Element;
}

const languagesVarsCodes = [
  "LANG_neobooking_cart_deleting_in_progress", //Suppression de votre panier en cours…
  "LANG_neobooking_cart_now_empty", //Votre panier est maintenant vide !
  "LANG_neobooking_please_wait", //Veuillez patienter quelques instants
  "LANG_neobooking_my_cart", //Mon panier
  "LANG_neobooking_close", //Fermer
  "LANG_neobooking_cart_delete", //Supprimer mon panier
  "LANG_neobooking_total", //Total
  "LANG_neobooking_continue", //Continuer
]

const CartOverlay: React.FC<CartOverlayProps> = ({ btnElement }) => {

  /* Hooks */
  const { getLanguageVarValue } = useSession();
  const { cart, clear } = useCart();

  /* State */
  const [open, setOpen] = useState<boolean>(false)
  const [onClearBasket, setOnClearBasket] = useState<boolean>(false)

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleClearBasket = () => {
    setOnClearBasket(true)
    const toastClear = toast.loading(getLanguageVarValue("LANG_neobooking_cart_deleting_in_progress"))
    clear((err) => {
      if (err) {
        toast.error(err.message, {
          id: toastClear
        });
      }
      else {
        toast.success(getLanguageVarValue("LANG_neobooking_cart_now_empty"), {
          id: toastClear
        });
        handleClose()
      }
      setOnClearBasket(false)
    })
  }

  useLoadingScreen(onClearBasket, getLanguageVarValue("LANG_neobooking_cart_deleting_in_progress"), getLanguageVarValue("LANG_neobooking_please_wait"))

  return <>
    {
      (cart && cart?.reservations?.length!! > 0 && !cart.fromFastCheckinCheckout) && <>
        {btnElement(handleOpen)}
        <Transition.Root show={open} as={Fragment}>
          <Dialog as="div" style={{ zIndex: 1050 }} className="tw-fixed tw-inset-0 tw-overflow-hidden" onClose={setOpen}>
            <div className="tw-absolute tw-inset-0 tw-overflow-hidden">
              <Transition.Child
                as={Fragment}
                enter="tw-ease-in-out tw-duration-500"
                enterFrom="tw-opacity-0"
                enterTo="tw-opacity-100"
                leave="tw-ease-in-out tw-duration-500"
                leaveFrom="tw-opacity-100"
                leaveTo="tw-opacity-0"
              >
                <Dialog.Overlay className="tw-absolute tw-inset-0 tw-bg-gray-500 tw-bg-opacity-75 tw-transition-opacity" />
              </Transition.Child>
              <div className="tw-pointer-events-none tw-fixed tw-inset-y-0 tw-right-0 tw-flex tw-max-w-full tw-pl-10">
                <Transition.Child
                  as={Fragment}
                  enter="tw-transform tw-transition tw-ease-in-out tw-duration-500 sm:tw-duration-700"
                  enterFrom="tw-translate-x-full"
                  enterTo="tw-translate-x-0"
                  leave="tw-transform tw-transition tw-ease-in-out tw-duration-500 sm:tw-duration-700"
                  leaveFrom="tw-translate-x-0"
                  leaveTo="tw-translate-x-full"
                >
                  <div className="tw-pointer-events-auto tw-w-screen tw-max-w-md">
                    <div className="tw-flex tw-h-full tw-flex-col tw-bg-white tw-shadow-xl">
                      <div className="tw-flex-1 tw-overflow-y-auto tw-pt-4 tw-px-4">
                        <div className="tw-flex tw-items-start tw-justify-between">
                          <Dialog.Title className="tw-text-lg tw-font-medium tw-text-gray-900"> {getLanguageVarValue("LANG_neobooking_my_cart")} </Dialog.Title>
                          <div className="tw-ml-3 tw-flex tw-h-7 tw-items-center">
                            <button
                              title={getLanguageVarValue("LANG_neobooking_close")}
                              type="button"
                              className="tw--m-2 tw-p-2 tw-text-gray-400 hover:tw-text-gray-500"
                              onClick={() => setOpen(false)}
                            >
                              <span className="tw-sr-only">{getLanguageVarValue("LANG_neobooking_close")}</span>
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        </div>
                        <div className="tw-mt-8">
                          <div className="tw-flow-root">
                            <BasketSummary enableEdit={!cart.fromFastCheckinCheckout} reservations={cart?.reservations!!} border={false} onCloseSlideMenu={handleClose} />
                          </div>
                        </div>
                      </div>
                      <div className="tw-border-t tw-border-gray-200 tw-py-4 tw-px-4">
                        <div className="tw-flex tw-justify-between tw-text-base tw-font-medium tw-text-gray-900">
                          <p>{getLanguageVarValue("LANG_neobooking_total")}</p>
                          <p>{getTotalReservationsPrice(cart?.reservations)}</p>
                        </div>
                        <div className="tw-mt-6">
                          <Link to={routes.reservation} onClick={() => setOpen(false)} className="tw-text-center tw-block tw-w-full tw-text-sm tw-rounded tw-px-3 tw-py-2 tw-font-medium focus:tw-outline-none tw-cursor-pointer tw-border tw-border-primary-500 tw-bg-white tw-text-primary-500 hover:tw-bg-primary-500 hover:tw-border-white hover:tw-text-white tw-transition-colors tw-duration-200 tw-ease-in-out">
                            {getLanguageVarValue("LANG_neobooking_continue")}
                          </Link>
                        </div>
                        <div className="tw-mt-4 tw-flex tw-justify-between tw-text-center tw-text-sm tw-text-gray-500">
                          <button
                            type="button"
                            className="tw-font-medium "
                            onClick={() => setOpen(false)}
                          >
                            {getLanguageVarValue("LANG_neobooking_close")}
                          </button>
                          <Button type='button' loadingChildren={
                            <>
                              {getLanguageVarValue("LANG_neobooking_deleting_in_progress")}
                            </>
                          }
                            onClick={handleClearBasket}
                            variant="danger"
                            shape='link'
                            additionalClassname='tw-font-medium'>{getLanguageVarValue("LANG_neobooking_cart_delete")}</Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      </>
    }
  </>
}

export default CartOverlay;